import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import ChevronDown from './icons/ChevronDown';
import CloseIcon from './icons/CloseIcon';
import SearchIcon from './icons/SearchIcon';

/**
 * A flexible dropdown menu component with search, infinite scroll, and portal support.
 *
 * Features:
 * - Local and remote search capabilities
 * - Infinite scrolling for large datasets
 * - Portal rendering for better z-index management
 * - Customizable styling and positioning
 * - Loading states and error handling
 * - Clear selection functionality
 * - Responsive design with automatic positioning
 *
 * @component
 * @example
 * // Basic usage
 * <DropdownMenu
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' }
 *   ]}
 *   onSelect={(option) => console.log(option)}
 *   placeholder="Select an option"
 * />
 *
 * @example
 * // With search and infinite scroll
 * <DropdownMenu
 *   options={options}
 *   onSelect={handleSelect}
 *   onSearch={handleSearch}
 *   hasMore={hasMore}
 *   loadMore={loadMore}
 *   isLoading={isLoading}
 *   searchable={true}
 *   localSearch={false}
 * />
 *
 * @example
 * // With label and required field
 * <DropdownMenu
 *   options={options}
 *   onSelect={handleSelect}
 *   label="Select Country"
 *   required={true}
 *   placeholder="Choose a country"
 * />
 *
 * @example
 * // Disabled dropdown
 * <DropdownMenu
 *   options={options}
 *   onSelect={handleSelect}
 *   placeholder="This dropdown is disabled"
 *   disabled={true}
 * />
 *
 * @param {Object} props - The component props
 * @param {Array<Object>} [props.options=[]] - Array of dropdown options
 * @param {string|number} props.options[].value - Unique value for the option
 * @param {string} props.options[].label - Display text for the option
 * @param {string} [props.options[].description] - Optional description text shown below the label
 * @param {string} [props.options[].badge] - Optional badge text shown on the right
 * @param {string} [props.placeholder='Select an option'] - Placeholder text when no option is selected
 * @param {Function} [props.onSelect=() => {}] - Callback function called when an option is selected. Receives the selected option object or null when cleared
 * @param {Object|string|number|null} [props.value=null] - Currently selected option.
 *
 *   Behavior depends on how data is loaded:
 *   - **Paginated / remote data**: Pass the full option object (`{ label, value }`). The dropdown will display this directly without validating whether it exists in the current page of results. This allows showing the default selection even when its option is not yet loaded.
 *   - **Non-paginated / fully loaded data**:
 *     - **Recommended**: Pass a primitive value (`string` or `number`). The dropdown will validate the value against the available options to determine the correct label to display.
 *     - **Alternative**: Pass an object (`{ label, value }`). The dropdown will display it directly without validation, which may lead to displaying invalid or stale selections if the object doesn't exist in the options array.
 *
 * @param {boolean} [props.searchable=true] - Whether to show search input in dropdown
 * @param {boolean} [props.hideCloseOption=false] - Whether to hide the clear/close button when an option is selected
 * @param {string} [props.className=''] - Additional CSS classes for the container div
 * @param {string} [props.triggerButtonClassName=''] - Additional CSS classes for the trigger button
 * @param {boolean} [props.disablePortal=true] - Whether to disable portal rendering (renders inline when true)
 * @param {string} [props.portalClassName=''] - CSS classes for the portal container when portal is enabled
 * @param {number} [props.maxDropdownHeight=300] - Maximum height of dropdown content in pixels
 * @param {boolean} [props.required=false] - Whether the field is required (shows red asterisk next to label)
 * @param {string} [props.label=''] - Label text displayed above the dropdown
 * @param {string} [props.labelClassName=''] - Additional CSS classes for the label element
 * @param {Function} [props.onSearch=null] - Callback for search input changes (for remote search). Receives the search term as parameter
 * @param {number} [props.searchDebounceMs=300] - Debounce time for search in milliseconds (only applies to remote search)
 * @param {boolean} [props.localSearch=false] - Whether to perform local filtering of options (true) or use remote search (false)
 * @param {boolean} [props.isLoading=false] - Whether data is currently loading (shows loading spinner)
 * @param {boolean} [props.hasMore=false] - Whether more items can be loaded via infinite scroll
 * @param {Function} [props.loadMore=() => {}] - Callback to load more items for infinite scroll. Called when user scrolls to bottom
 * @param {Object|null} [props.error=null] - Error object if an error occurred during loading
 * @param {boolean} [props.disabled=false] - Whether the dropdown is disabled (prevents interaction and applies disabled styling)
 * @param {boolean} [props.clearDropdownOnSelect=false] - Whether to clear the dropdown selection after each selection (useful for multi-action dropdowns)
 * @param {Function} [props.onOpen=null] - Callback function called when the dropdown is opened
 *
 * @returns {JSX.Element} The rendered dropdown menu component
 */
const DropdownMenu = ({
  options = [],
  placeholder = 'Select an option',
  onSelect = () => {},
  value = null,
  searchable = true,
  hideCloseOption = false,
  className = '',
  triggerButtonClassName = '',
  disablePortal = true,
  portalClassName = '',
  maxDropdownHeight = 300,
  required = false,
  label = '',
  labelClassName = '',
  onSearch = null,
  searchDebounceMs = 300,
  localSearch = false,
  isLoading = false,
  hasMore = false,
  loadMore = () => {},
  disabled = false,
  clearDropdownOnSelect = false,
  error = null,
  onOpen = null,
}) => {
  const DROPDOWN_HEIGHT = maxDropdownHeight; // Max dropdown height in pixels

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState(value);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [portalStyles, setPortalStyles] = useState({});
  const [isSearching, setIsSearching] = useState(false);

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const triggerRef = useRef(null);
  const portalRef = useRef(null);
  const searchDebounceTimerRef = useRef(null);
  const isFirstRender = useRef(true);

  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    isLoading,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
    disabled: Boolean(error),
  });

  // Normalize: if parent passes a non-object value, find the full option object
  const normalizedValue = useMemo(() => {
    if (value == null) return null;
    if (typeof value === 'object') return value;
    return options.find((opt) => opt.value === value) || null;
  }, [value, options]);

  const isControlled = value !== undefined && value !== null;
  const selected = isControlled ? normalizedValue : selectedOption;

  // Derived (not stateful) list of options to render.
  // Using useMemo instead of useState+useEffect avoids an extra render cycle
  // and can never trigger a setState-driven render loop, even if the parent
  // passes a new `options` array reference on every render.
  const filteredOptions = useMemo(() => {
    if (!localSearch) return options;
    if (searchTerm.trim() === '') return options;
    return options.filter((option) => {
      const labelMatch = option.label
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      const descriptionMatch = option.description
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      return labelMatch || descriptionMatch;
    });
  }, [options, localSearch, searchTerm]);

  useEffect(() => {
    if (normalizedValue?.value !== selectedOption?.value) {
      setSelectedOption(normalizedValue);
    }
  }, [normalizedValue]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, searchable]);

  // Debounced search effect - only for API/parent component search
  useEffect(() => {
    // Skip if local search is enabled
    if (localSearch) return;

    // Clear any existing timer
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    // Only trigger search if onSearch callback is provided
    if (onSearch && !isFirstRender.current) {
      // Set searching state when user types
      setIsSearching(true);

      searchDebounceTimerRef.current = setTimeout(() => {
        onSearch(searchTerm);
      }, searchDebounceMs);
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Cleanup timer on unmount or when dependencies change
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchTerm, onSearch, searchDebounceMs, localSearch]);

  // Clear searching state when loading is complete (for API search only)
  useEffect(() => {
    if (!localSearch && !isLoading && options) {
      setIsSearching(false);
    }
  }, [isLoading, localSearch, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideDropdown =
        (dropdownRef.current && dropdownRef.current.contains(event.target)) ||
        (portalRef.current && portalRef.current.contains(event.target));

      const clickedTrigger =
        triggerRef.current && triggerRef.current.contains(event.target);

      if (!clickedInsideDropdown && !clickedTrigger) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Compute portal positioning when dropdown opens (or on resize/scroll)
  useEffect(() => {
    const compute = () => {
      if (!disablePortal && isOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        const spaceBelow = windowHeight - rect.bottom;
        const spaceAbove = rect.top;

        const shouldOpenAbove =
          spaceBelow < DROPDOWN_HEIGHT && spaceAbove > DROPDOWN_HEIGHT;

        const top = shouldOpenAbove ? rect.top - 8 : rect.bottom + 8;
        const left = rect.left;
        const width = rect.width;

        // Calculate max height to prevent overflow
        const maxHeight = shouldOpenAbove
          ? Math.min(DROPDOWN_HEIGHT, spaceAbove - 16) // 16px buffer
          : Math.min(DROPDOWN_HEIGHT, spaceBelow - 16);

        setPortalStyles({
          position: 'fixed',
          top: `${Math.max(0, top)}px`,
          left: `${left}px`,
          width: `${width}px`,
          maxHeight: `${maxHeight}px`,
          zIndex: 9999,
        });

        const newPosition = shouldOpenAbove ? 'top' : 'bottom';
        if (newPosition !== dropdownPosition) {
          setDropdownPosition(newPosition);
        }
      }
    };

    compute();

    if (!disablePortal && isOpen) {
      window.addEventListener('resize', compute);
      window.addEventListener('scroll', compute, true);
    }

    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [disablePortal, isOpen, dropdownPosition, DROPDOWN_HEIGHT]);

  const handleSelect = (option) => {
    if (!isControlled) setSelectedOption(option);
    onSelect(option);

    // Clear dropdown after selection if prop is enabled
    if (clearDropdownOnSelect) {
      setIsOpen(false);
      setSearchTerm('');
      setSelectedOption(null);
    } else {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    setSelectedOption(null);
    onSelect(null);
  };

  const toggleDropdown = () => {
    if (disabled) return;

    if (!isOpen) {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        const spaceBelow = windowHeight - rect.bottom;
        const spaceAbove = rect.top;

        const position =
          spaceBelow < DROPDOWN_HEIGHT && spaceAbove > DROPDOWN_HEIGHT
            ? 'top'
            : 'bottom';
        setDropdownPosition(position);

        if (!disablePortal) {
          const top = position === 'top' ? rect.top - 8 : rect.bottom + 8;
          const left = rect.left;
          const width = rect.width;

          // Calculate max height based on available space
          const maxHeight =
            position === 'top'
              ? Math.min(DROPDOWN_HEIGHT, spaceAbove - 16)
              : Math.min(DROPDOWN_HEIGHT, spaceBelow - 16);

          setPortalStyles({
            position: 'fixed',
            top: `${Math.max(0, top)}px`,
            left: `${left}px`,
            width: `${width}px`,
            maxHeight: `${maxHeight}px`,
            zIndex: 9999,
          });
        }
      }

      setIsOpen(true);
      setSearchTerm('');
      if (onOpen) onOpen();
    } else {
      setIsOpen(false);
    }
  };

  const getDropdownMenuClass = () => {
    const baseClasses =
      'z-50 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden origin-top';
    const portalClasses = disablePortal ? 'absolute w-full' : '';

    let positionClasses = '';
    if (dropdownPosition === 'bottom') {
      positionClasses = disablePortal ? 'mt-2 top-full' : '';
    } else {
      if (disablePortal) {
        positionClasses = label ? '-mb-5 bottom-full' : 'mb-2 bottom-full';
      }
    }

    return `${portalClasses} ${baseClasses} ${positionClasses}`.trim();
  };

  return (
    <div className={`relative w-full ${className}`}>
      {label && (
        <label
          className={`block text-gray-700 text-base font-bold mb-2 ${labelClassName}`}
        >
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className={`
            w-full px-3 py-1.5 border rounded-lg text-left
            transition-all duration-300 ease-out
            ${
              disabled
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                : `bg-white 
                 hover:border-orange-300 hover:shadow-md
                 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500
                 ${isOpen ? 'border-orange-500 shadow-lg' : 'border-gray-400'}
                 ${selected ? 'text-gray-900' : 'text-gray-500'}`
            }
            ${triggerButtonClassName}
            ${error ? 'border-red-500' : ''}
          `}
      >
        <div className="flex items-center justify-between">
          <span className={`truncate text-base p-1`}>
            {selected ? selected.label : placeholder}
          </span>
          <div className="flex items-center space-x-2">
            {selected && !hideCloseOption && !disabled && (
              <button
                onClick={clearSelection}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <CloseIcon className="w-4 h-4 text-gray-400" />
              </button>
            )}
            <ChevronDown
              className={`w-5 h-5 transition-transform duration-300 ${
                disabled ? 'text-gray-300' : 'text-gray-400'
              } ${isOpen && !disabled ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
      </button>

      {error && (
        <p className="mt-1 text-base text-red-500">
          {typeof error === 'object' ? error.message : error}
        </p>
      )}

      {/* Dropdown Menu (inline or portal) */}
      {(() => {
        const menu = (
          <div
            ref={!disablePortal ? portalRef : dropdownRef}
            className={getDropdownMenuClass()}
            style={
              !disablePortal
                ? {
                    ...portalStyles,
                    ...(dropdownPosition === 'top'
                      ? { transform: 'translateY(-100%)' }
                      : {}),
                  }
                : {}
            }
          >
            {/* Search Input */}
            {searchable && (
              <div className="p-3 border-b border-gray-100">
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-colors text-base"
                  />
                </div>
              </div>
            )}

            {/* Options List */}
            <div
              ref={rootRef}
              className="overflow-y-auto"
              style={{
                maxHeight:
                  !disablePortal && portalStyles.maxHeight
                    ? `calc(${portalStyles.maxHeight} - ${searchable ? '60px' : '0px'})` // Subtract search input height
                    : `${DROPDOWN_HEIGHT}px`,
              }}
            >
              {!localSearch && isSearching ? (
                <div className="py-8">
                  <Loader />
                </div>
              ) : filteredOptions?.length > 0 ? (
                <div className="py-2">
                  <div>
                    {filteredOptions.map((option, index) => (
                      <button
                        key={option.id || option.value || index}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className={`
                        w-full px-4 py-3 text-left hover:bg-indigo-50 hover:text-indigo-700
                        transition-all duration-200 ease-out
                        ${
                          selected?.value === option.value
                            ? 'bg-indigo-100 text-indigo-700 font-medium'
                            : 'text-gray-700'
                        }
                      `}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-base">
                              {option.label}
                            </div>
                            {option.description && (
                              <div className="text-sm text-gray-500 mt-1">
                                {option.description}
                              </div>
                            )}
                          </div>
                          {option.badge && (
                            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                              {option.badge}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  {hasMore && (
                    <div ref={infiniteRef}>
                      <Loader />
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <SearchIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>No options found</p>
                </div>
              )}
            </div>
          </div>
        );

        if (!disablePortal && isOpen) {
          return createPortal(
            <div className={portalClassName || ''}>{menu}</div>,
            document.body
          );
        }

        return isOpen ? menu : null;
      })()}
    </div>
  );
};

export default DropdownMenu;

// Prop types
DropdownMenu.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      badge: PropTypes.string,
    })
  ).isRequired,
  placeholder: PropTypes.string,
  itemsPerPage: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      badge: PropTypes.string,
    }),
    PropTypes.oneOf([null]),
  ]),
  searchable: PropTypes.bool,
  hideCloseOption: PropTypes.bool,
  className: PropTypes.string,
  triggerButtonClassName: PropTypes.string,
  disablePortal: PropTypes.bool,
  portalClassName: PropTypes.string,
  maxDropdownHeight: PropTypes.number,
  required: PropTypes.bool,
  label: PropTypes.string,
  labelClassName: PropTypes.string,
  onSearch: PropTypes.func,
  searchDebounceMs: PropTypes.number,
  localSearch: PropTypes.bool,
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  loadMore: PropTypes.func,
  disabled: PropTypes.bool,
  clearDropdownOnSelect: PropTypes.bool,
  error: PropTypes.string,
  onOpen: PropTypes.func,
};

/**
 * Loading spinner component used within the dropdown menu
 *
 * @component
 * @returns {JSX.Element} A centered loading spinner with text
 */
const Loader = () => {
  return (
    <div className="p-1">
      <div role="status" className="flex justify-center pt-2">
        <svg
          aria-hidden="true"
          className="w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-blue-950"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">Loading...</span>
      </div>
      <div className="flex items-center justify-center text-gray-500 text-sm mt-2">
        Loading...
      </div>
    </div>
  );
};
